import type React from "react"
import { useMemo } from "react"
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { CardColumnWithCustomPadding } from "./style"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { DollarSign, ChevronDown } from "lucide-react"
import type { IOneStoreConciliation } from "@/@types/store-conciliation"
import { formatToBRL } from "@/utils/formatToMoney"
import type { IDTEFPaymentResponse } from "@/@types/dtef-payment"
import { DtefComparisonRowWithFallback } from "../../dtef/components/dtef-comparison-row-with-fallback"
import DemonstrativeMovements from "../demonstrative-movements"
import DemonstrativeExport from "../demonstrative-export"
import { DtefSourcesWarning } from "../../dtef/components/dtef-sources-warning"
import { DTEF_CATEGORY_LABELS_RESUME } from "../../dtef/constants"
import {
  parseAmount,
  isSoftway,
  isValidItem,
  mapToCard,
  TYPE_FATURA_AVENIDA,
  TYPE_CARTAO_AVENIDA,
  TYPE_DEBITO,
  TYPE_CREDITO,
  type ITefCard,
} from "./utils"

interface TransactionResumeProps {
  setIsCollapsibleOpen: React.Dispatch<React.SetStateAction<boolean>>
  conciliation: IOneStoreConciliation | undefined
  dtefData: IDTEFPaymentResponse | undefined
}

export default function TransactionResume({ setIsCollapsibleOpen, conciliation, dtefData }: TransactionResumeProps) {
  const data = conciliation

  const { debitCards, creditCards, softwayData, pixDtef } = useMemo(() => {
    const emptyResult = {
      debitCards: [] as ITefCard[],
      creditCards: [] as ITefCard[],
      softwayData: { faturaAvenida: 0, cartaoAvenida: 0 },
      pixDtef: 0,
    }

    if (!dtefData) return emptyResult

    const payments = dtefData.dtef_payment || []
    const validItems = payments.filter(isValidItem)
    const softwayItems = validItems.filter(isSoftway)
    const regularItems = validItems.filter((item) => !isSoftway(item))

    const faturaItem = softwayItems.find((i) => i.type_operation === TYPE_FATURA_AVENIDA)
    const cartaoItem = softwayItems.find((i) => i.type_operation === TYPE_CARTAO_AVENIDA)

    const sortByBrand = (a: ITefCard, b: ITefCard) =>
      a.bandeira.trim().localeCompare(b.bandeira.trim())

    return {
      debitCards: regularItems.filter((i) => i.type_operation === TYPE_DEBITO).map(mapToCard).sort(sortByBrand),
      creditCards: regularItems.filter((i) => i.type_operation === TYPE_CREDITO).map(mapToCard).sort(sortByBrand),
      softwayData: {
        faturaAvenida: faturaItem ? parseAmount(faturaItem.total_amount) : 0,
        cartaoAvenida: cartaoItem ? parseAmount(cartaoItem.total_amount) : 0,
      },
      pixDtef: dtefData.pix_dtef ? parseAmount(dtefData.pix_dtef.total_amount) : 0,
    }
  }, [dtefData])

  const hasDtefCards = debitCards.length > 0 || creditCards.length > 0

  const handleCollapsibleChange = (open: boolean) => {
    if (open) {
      setIsCollapsibleOpen(true)
    }
  }

  return (
    <Card className="w-full lg:w-2/3 min-w-0 overflow-hidden shadow-none">
      <CardHeader>
        <div className="flex w-full justify-between items-center">
          <div className="flex gap-2 sm:gap-4 items-center font-bold text-[#2B0C1A] text-[12px] sm:text-[14px]">
            <DollarSign color="#2B0C1A" size={16} />
            <span className="hidden sm:inline whitespace-nowrap">Resumo das Transações da Loja</span>
            <span className="sm:hidden">Resumo Transações</span>
          </div>
          <DemonstrativeExport conciliation_id={conciliation?.id?.toString()} />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 text-[12px] sm:text-[14px] text-[#444444]">
        {/* First section with better responsive grid */}
        <div className="border w-full rounded overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-300">
            <div className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4">
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Devoluções:</p>
                <p className="font-[300] text-right ml-2 shrink-0">{formatToBRL(data?.summary?.returns || 0)}</p>
              </div>
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Trocas:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.exchanges || 0)}</p>
              </div>
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Venda a vista:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.sales || 0)}</p>
              </div>
            </div>

            <div className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4">
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Total TEF:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.total_tef || 0)}</p>
              </div>
              <DtefComparisonRowWithFallback
                label={DTEF_CATEGORY_LABELS_RESUME.digital_wallet}
                comparison={data?.dtef_comparison?.comparisons.digital_wallet}
                fallbackVarValue={data?.summary?.total_pix || 0}
                fallbackDtefValue={pixDtef}
              />
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Dinheiro:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.total_money || 0)}</p>
              </div>
              {data?.dtef_comparison?.comparisons.third_party_cards ? (
                <DtefComparisonRowWithFallback
                  label={DTEF_CATEGORY_LABELS_RESUME.third_party_cards}
                  comparison={data.dtef_comparison.comparisons.third_party_cards}
                  fallbackVarValue={0}
                  fallbackDtefValue={0}
                />
              ) : (
                <div className="flex justify-between items-start">
                  <p className="font-semibold break-words">
                    {DTEF_CATEGORY_LABELS_RESUME.third_party_cards}:
                  </p>
                  <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.total_cards || 0)}</p>
                </div>
              )}
            </div>

            <div className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4 sm:col-span-2 lg:col-span-1">
              <DtefComparisonRowWithFallback
                label={DTEF_CATEGORY_LABELS_RESUME.avenida_bill}
                comparison={data?.dtef_comparison?.comparisons.avenida_bill}
                fallbackVarValue={data?.summary?.avenida_bill || 0}
                fallbackDtefValue={softwayData.faturaAvenida}
              />
              <DtefComparisonRowWithFallback
                label={DTEF_CATEGORY_LABELS_RESUME.avenida_card}
                comparison={data?.dtef_comparison?.comparisons.avenida_card}
                fallbackVarValue={data?.summary?.avenida_card || 0}
                fallbackDtefValue={softwayData.cartaoAvenida}
              />
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Sabadão premiado:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.sabadao || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible section with improved responsive layout */}
        <Collapsible className="w-full border rounded p-2 sm:p-4" onOpenChange={handleCollapsibleChange}>
          <CollapsibleTrigger className="w-full">
            <div className="flex flex-col sm:flex-row gap-2 justify-between">
              <div className="flex gap-2 items-center pb-2 sm:pb-0 border-b sm:border-b-0 sm:border-r border-gray-300 w-full sm:pr-4">
                <p className="font-semibold text-sm break-words">Cartões de Débito (Terceiros)</p>
                <ChevronDown size={16} className="shrink-0" />
              </div>
              <div className="flex gap-2 w-full items-center justify-start sm:justify-end">
                <p className="font-semibold text-sm break-words">Cartões de Crédito (Terceiros)</p>
                <ChevronDown size={16} className="shrink-0" />
              </div>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-12 my-2">
            <div className="flex flex-col items-start justify-start w-full mb-4 lg:mb-0">
              <h4 className="font-semibold mb-2 lg:hidden">Cartões de Débito</h4>
              {data?.summary?.debt_cards?.map((card: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col items-start justify-between my-1 gap-1 w-full border p-2 sm:p-3 rounded-md"
                >
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Bandeira:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.bandeira.trim()}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Quantidade:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.quantidade}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Rede Adquirente:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.redeAdquirente}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Valor Total:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{formatToBRL(card.valorTotal)}</span>
                  </div>
                </div>
              ))}
              {!data?.summary?.debt_cards && <p className="text-sm text-gray-500">Nenhum cartão</p>}
            </div>

            <div className="flex flex-col items-start justify-start w-full">
              <h4 className="font-semibold mb-2 lg:hidden">Cartões de Crédito</h4>
              {data?.summary?.credit_cards?.map((card: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col items-start justify-between my-1 gap-1 w-full border p-2 sm:p-3 rounded-md"
                >
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Bandeira:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.bandeira.trim()}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Quantidade:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.quantidade}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Rede Adquirente:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{card.redeAdquirente}</span>
                  </div>
                  <div className="flex gap-2 w-full justify-between text-sm">
                    <p className="font-semibold break-words">Valor Total:</p>
                    <span className="font-[300] text-right ml-2 shrink-0">{formatToBRL(card.valorTotal)}</span>
                  </div>
                </div>
              ))}
              {!data?.summary?.credit_cards && <p className="text-sm text-gray-500">Nenhum Cartão</p>}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* DTEF Collapsible section */}
        {hasDtefCards && (
          <Collapsible className="w-full border rounded p-2 sm:p-4" onOpenChange={handleCollapsibleChange}>
            <CollapsibleTrigger className="w-full">
              <div className="flex flex-col sm:flex-row gap-2 justify-between">
                <div className="flex gap-2 items-center pb-2 sm:pb-0 border-b sm:border-b-0 sm:border-r border-gray-300 w-full sm:pr-4">
                  <p className="font-semibold text-sm break-words">DTEF Débito</p>
                  <ChevronDown size={16} className="shrink-0" />
                </div>
                <div className="flex gap-2 w-full items-center justify-start sm:justify-end">
                  <p className="font-semibold text-sm break-words">DTEF Crédito</p>
                  <ChevronDown size={16} className="shrink-0" />
                </div>
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-12 my-2">
              <DtefSourcesWarning sources={dtefData?.sources} />
              <div className="flex flex-col items-start justify-start w-full mb-4 lg:mb-0">
                <h4 className="font-semibold mb-2 lg:hidden">DTEF Débito</h4>
                {debitCards.map((card, index) => (
                  <div
                    key={`dtef-debit-${index}`}
                    className="flex flex-col items-start justify-between my-1 gap-1 w-full border p-2 sm:p-3 rounded-md"
                  >
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Bandeira:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.bandeira.trim()}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Quantidade:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.quantidade}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Rede Adquirente:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.redeAdquirente}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Valor Total:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{formatToBRL(parseAmount(card.valorTotal))}</span>
                    </div>
                  </div>
                ))}
                {debitCards.length === 0 && <p className="text-sm text-gray-500">Nenhum cartão</p>}
              </div>

              <div className="flex flex-col items-start justify-start w-full">
                <h4 className="font-semibold mb-2 lg:hidden">DTEF Crédito</h4>
                {creditCards.map((card, index) => (
                  <div
                    key={`dtef-credit-${index}`}
                    className="flex flex-col items-start justify-between my-1 gap-1 w-full border p-2 sm:p-3 rounded-md"
                  >
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Bandeira:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.bandeira.trim()}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Quantidade:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.quantidade}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Rede Adquirente:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{card.redeAdquirente}</span>
                    </div>
                    <div className="flex gap-2 w-full justify-between text-sm">
                      <p className="font-semibold break-words">Valor Total:</p>
                      <span className="font-[300] text-right ml-2 shrink-0">{formatToBRL(parseAmount(card.valorTotal))}</span>
                    </div>
                  </div>
                ))}
                {creditCards.length === 0 && <p className="text-sm text-gray-500">Nenhum Cartão</p>}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Deposits section with responsive grid */}
        <div className="border w-full rounded overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-300">
            <CardColumnWithCustomPadding className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4">
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Depósito:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.deposits || 0)}</p>
              </div>
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Depósito dia seguinte:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.next_day_deposits || 0)}</p>
              </div>
            </CardColumnWithCustomPadding>

            <div className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4">
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Saldo composição:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.composition_balance || 0)}</p>
              </div>
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Saldo dia anterior:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.previous_day_balance || 0)}</p>
              </div>
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Composição dinheiro:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.summary?.composition_money || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border w-full rounded overflow-hidden">
          <div className="grid grid-cols-1 divide-y divide-gray-300">
            <div className="w-full p-2 sm:p-3 flex flex-col gap-2 sm:gap-4">
              <div className="flex justify-between items-start">
                <p className="font-semibold break-words">Total de divergências:</p>
                <p className="text-right ml-2 shrink-0">{formatToBRL(data?.total_divergences_sum || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        <DemonstrativeMovements demonstrative={data?.summary?.demonstrative} />
      </CardContent>
    </Card>
  )
}
